/**
 * A follow-up is a real automation. The board reads and writes it through the
 * automations API the builder uses, so there is one representation of a
 * follow-up in the whole product: the row, its steps, and the graph both
 * surfaces draw from them.
 */
import type { EStep, Editor } from "../components/FlowEditor";
import type { LibraryKind } from "../../shared/library-kind";
import type { BoardState } from "../../shared/types";
import { plainToRich } from "../components/RichBox";

/** The email step a follow-up leads with: the subject and body the card edits. */
export function emailOf(editor: Editor): Extract<EStep, { type: "email" }> | null {
  const step = editor.steps.find((s) => s.type === "email");
  return step && step.type === "email" ? step : null;
}

/** The same steps with one email rewritten, ready to save. */
export function withEmail(
  editor: Editor,
  patch: { subject?: string; body?: string },
): Editor {
  let done = false;
  return {
    ...editor,
    steps: editor.steps.map((s) => {
      if (done || s.type !== "email") return s;
      done = true;
      // The written html is what the engine sends, so a body patch has to reach
      // it. Setting the plain field alone would look saved and send nothing.
      const body = patch.body;
      return {
        ...s,
        subject: patch.subject ?? s.subject,
        ...(body === undefined ? {} : { preview: body, previewHtml: plainToRich(body) }),
      };
    }),
  };
}

/**
 * What the board holds for the follow-up the host has open. Nothing is written
 * on its own: edits stay in the editor until Done, which writes the whole
 * follow-up, and Discard puts the row's last accepted words back.
 */
export interface FollowUpBinding {
  automationId: string;
  editor: Editor | null;
  error: string;
  saving: boolean;
  /** True while the editor differs from what the row last accepted. */
  dirty: boolean;
  /** Edit the email in place. */
  setEmail(patch: { subject?: string; body?: string }): void;
  /** The graph's own edits (a step added, a wait changed, the trigger). */
  setEditor(next: Editor | ((prev: Editor) => Editor)): void;
  /** Write the row now: Done. Resolves once the write is back, so the flow sends the next one after it. */
  save(): Promise<void>;
  /** Put the words back the way the row has them. */
  discard(): void;
}

// ── writing one again ─────────────────────────────────────────────────────
/** The requests behind the follow-ups every board starts with. */
const DEFAULT_REQUESTS: ReadonlyArray<[string, string]> = [
  [
    "Confirm the registration",
    "Confirm their registration: tell them they are in, what the talk gives them, and where their private page is.",
  ],
  [
    "Follow up on the offer",
    "A day after they reach the offer without signing up, offer it once more, with the offer itself in the email.",
  ],
  ["Thank the people who finished", "Right after they finish the talk, thank them and name the one thing to do this week."],
  ["Remind them an hour before", "Remind them an hour before the session starts, with their link."],
  [
    "Nudge the people who never watched",
    "Two days after they register, if they have not watched, nudge them to start.",
  ],
];

/**
 * The request that writes a follow-up called `label` again. A known name gets
 * the request it was born from; any other name gets a plain one that carries
 * the name and the last subject, so the model knows what it is replacing.
 */
export function requestFor(label: string, subject: string): string {
  const name = label.trim().toLowerCase();
  const known = [...DEFAULT_REQUESTS, ...FOLLOW_UP_LIBRARY.map(([n, request]) => [n, request] as [string, string])].find(
    ([n]) => n.toLowerCase() === name,
  );
  if (known) return known[1];
  const was = subject.trim() ? ` Its last email was called "${subject.trim()}".` : "";
  return `Write the follow-up called "${label.trim()}" again.${was}`;
}

/**
 * The ready-made follow-ups: a plain-English request the producer answers from
 * the talk, and the kind of follow-up it makes (so the board can tell when one
 * like it is already running, whoever built it). Two of them hang on a live
 * session, so an on-demand talk does not offer them.
 */
export const FOLLOW_UP_LIBRARY: ReadonlyArray<[string, string, LibraryKind, "live" | "any"]> = [
  [
    "Reminder before a live session",
    "Remind them an hour before the session starts, with their link.",
    "reminder",
    "live",
  ],
  [
    "Replay nudge, if unwatched",
    "Two days after they register, if they have not watched, nudge them to start.",
    "unwatched",
    "any",
  ],
  [
    "Offer follow-up, if no click",
    "A day after they reach the offer without clicking it, put the offer in front of them once more, with a link straight to it.",
    "no_click",
    "any",
  ],
  [
    "Thank you after finishing",
    "Right after they have finished the talk, thank them by name for staying to the end and point them at their own page, where everything from the talk stays.",
    "thanks",
    "any",
  ],
  ["No-show", "An hour after a session they missed, tell them the recording is on their page.", "unwatched", "live"],
];

/** Is a follow-up like this one already on the board? */
export function libraryTaken(
  state: BoardState,
  triggers: ReadonlyMap<string, string>,
  name: string,
  trigger: string,
): boolean {
  return state.mail.some((card) => {
    if (card.kind !== "mail") return false;
    if (card.label.trim().toLowerCase() === name.trim().toLowerCase()) return true;
    const own = card.automationId ? triggers.get(card.automationId) : undefined;
    return own === trigger;
  });
}
